import {html, LitElement} from 'lit'
import {customElement, state, property} from 'lit/decorators.js'
import {unsafeHTML} from 'lit/directives/unsafe-html.js'
import {repeat} from 'lit/directives/repeat.js';
import {i18nService} from '../../services/i18nService';
import {cookieService} from "../../services/cookieService";
import style from "./cookie-settings.scss?inline";
import {CookieDescription, DISABLE_OPT_IN_PARAM} from "./models";
import closeButton from "../../assets/images/svg/close_icon.svg";
import {addCountryToRelativeUrl, getUrlParam} from "./utils";
import {conf} from "../../config";

declare global {
  interface HTMLElementTagNameMap {
    'cookie-settings': CookieSettings
  }
}

@customElement('cookie-settings')
export class CookieSettings extends LitElement {

  @property() context: string = '';
  @state() i18nInitialized: boolean = false
  @state() cookieDescriptions: CookieDescription[] = [];

  private readonly cookieNotesUrl = `${conf.baseUrl}/legal/cookie-notice`;

  constructor() {
    super();
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
  }

  async connectedCallback() {
    super.connectedCallback();
    await i18nService.initTranslations();
    this.cookieDescriptions = await cookieService.loadCookieDescriptions(this.context);
    cookieService.notifyCookieSubscribers(this.cookieDescriptions)
    this.i18nInitialized = true;
    window.addEventListener('ny-cookie-settings-open', this.open)
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('ny-cookie-settings-open', this.open)
  }

  checkLeaveSettings = (event: FocusEvent) => {
    const nextElement = event.relatedTarget as HTMLElement | null;
    if (!nextElement) {
      this.close();
      return;
    }
    if (nextElement.nodeName == "COOKIE-BANNER" || nextElement.nodeName == "H2") {
      this.close();
    }
  }

  async open() {
    (this.renderRoot.querySelector('.cookie-settings-container') as HTMLDivElement).addEventListener('focusout', this.checkLeaveSettings)
    this.cookieDescriptions = await cookieService.loadCookieDescriptions(this.context);
    this.setAttribute('active', '');
    document.documentElement.style.overflowY = 'hidden';

    await this.updateComplete;

    const cookieHeader = this.renderRoot.querySelector('#cookie-settings-header') as HTMLHeadElement;
    if (cookieHeader) {
      setTimeout(() => {
        cookieHeader.focus();
      }, 10);
    }

    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && e.shiftKey){
        this.close();
        window.removeEventListener('keydown', handleEnter);
      } else if (e.key === 'Tab') {
        const header = this.renderRoot.querySelector('#cookie-settings-header') as HTMLHeadElement;
        header?.focus();
        window.removeEventListener('keydown', handleEnter);
      }
    };

    window.addEventListener('keydown', handleEnter);
  }

  close() {
    window.dispatchEvent(new Event('ny-cookie-settings-closed-refocus'))
    window.removeEventListener('focusout', this.checkLeaveSettings)
    this.removeAttribute('active');
    document.documentElement.removeAttribute('style');
  }

  acceptAllCookies() {
    cookieService.acceptAllCookies();
    this.close();
  }

  togglePermission(cd: CookieDescription) {
    cd.userPermission = !cd.userPermission;
  }

  updateCookies() {
    cookieService.updateCookies(this.cookieDescriptions);
    cookieService.setLastSeen(new Date());
    this.close();
  }

  renderInfoText() {
    const infoTranslation = i18nService.translate("cookie_settings.introduction.text.2", addCountryToRelativeUrl(this.cookieNotesUrl));
    return html`${unsafeHTML(infoTranslation)}`
  }

  render() {
    if (!this.i18nInitialized) return;
    return html`
      <style>${style}</style>
      <div class="closable-overlay" @click="${this.close}"></div>
      <div class="cookie-settings-container" id="cookie-settings-container" @keydown="${(e: KeyboardEvent) => {e.key==='Escape' ? this.close() : ''}}">
        <div class="cookie-settings-header">
          <h2 tabindex="0" id="cookie-settings-header">${i18nService.translate("cookie_settings.header.text")}</h2>
          <div class="cookie-settings-header-icon" @click="${this.close}" tabindex="0"
               @keydown="${(e: KeyboardEvent) => {e.key==='Enter' ? this.close() : ''}}">
            <img src="${closeButton}" alt="close">     
          </div>
        </div>
        <div class="cookie-settings-body">
          <div id="cookie-settings-introduction-desc" class="cookie-settings-introduction">
            <p>${i18nService.translate("cookie_settings.introduction.text.1")}</p>
            <p>${this.renderInfoText()}</p>
          </div>
          <div class="button-accept-all">
            <button aria-describedby="cookie-settings-introduction-desc" @click="${this.acceptAllCookies}">${i18nService.translate("cookie_settings.introduction.button.text")}</button>
          </div>
          <div class="cookie-settings-selection">
            ${repeat(this.cookieDescriptions, (cd) => cd.key, (cd) => html`
              <div class="cookie-container">
                <div class="cookie-checkbox">
                <input aria-describedby="${cd.key}-desc" type="checkbox" id="${cd.key}" name="${cd.key}" .checked=${cd.userPermission} aria-disabled="${cd.isRequired ? 'true' : 'false'}"
                       @click="${(e: Event) => cd.isRequired ? e.preventDefault() : this.togglePermission(cd)}">
                <label class="cookie-headline" for="${cd.key}">${i18nService.translate(cd.headlineText)}</label>
              </div>
              <p id="${cd.key}-desc" class="cookie-description">${unsafeHTML(i18nService.translate(cd.descriptionText, addCountryToRelativeUrl(cd.infoUrl)))}</p>
              </div>
            `)}
          </div>
        </div>
        <div class="cookie-settings-footer">
          <div class="button-save-selection">
            <button @click="${this.updateCookies}">${i18nService.translate("cookie_settings.footer.button.text")}</button>
          </div>
        </div>
      </div>
    `
  }
}
